import React from "react";
import styled from "styled-components";
import { Modal } from "@material-ui/core";
import theme from "../../../../assets/theme";
import * as XLSX from "xlsx";
import { PrimaryCTAButton } from "../../../common/Buttons";

const ValidationErrors = ({ modal, setModal }) => {
  const errorData = {
    duplicates: modal?.metaData?.duplicates?.length
      ? modal?.metaData?.duplicates.filter((item) => item !== "none") || []
      : [],
    missingKeys: modal?.metaData?.missingKeys?.length
      ? modal?.metaData?.missingKeys?.filter((item) => item !== "none") || []
      : [],
  };

  const totalErrors =
    errorData.duplicates.length + errorData.missingKeys.length;

  return (
    <Modal
      open={modal?.state}
      onClose={() => setModal({ state: false })}
      disableRestoreFocus={true}
    >
      <DrawerContainer>
        <Container>
          <Card>
            <Header>
              <HeaderIcon>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </HeaderIcon>
              <HeaderContent>
                <HeaderTitle>Validation Errors</HeaderTitle>
                <HeaderSubtitle>Please fix the following issues</HeaderSubtitle>
              </HeaderContent>
            </Header>

            <ContentWrapper>
              {/* Duplicates Section */}
              {errorData.duplicates && errorData.duplicates.length > 0 && (
                <Section>
                  <SectionHeader>
                    <SectionIcon type="duplicate">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#f59e0b"
                        strokeWidth="2"
                      >
                        <rect
                          x="9"
                          y="9"
                          width="13"
                          height="13"
                          rx="2"
                          ry="2"
                        />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </SectionIcon>
                    <SectionTitle>Duplicate Fields</SectionTitle>
                    <Badge type="duplicate">
                      {errorData.duplicates.length}
                    </Badge>
                  </SectionHeader>
                  <ItemList>
                    {errorData.duplicates.map((item, index) => (
                      <Item key={index} type="duplicate">
                        <ItemIcon type="duplicate" />
                        <ItemText type="duplicate">{item}</ItemText>
                      </Item>
                    ))}
                  </ItemList>
                </Section>
              )}

              {/* Missing Keys Section */}
              {errorData.missingKeys && errorData.missingKeys.length > 0 && (
                <Section>
                  <SectionHeader>
                    <SectionIcon type="missing">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="2"
                      >
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    </SectionIcon>
                    <SectionTitle>Missing Required Fields</SectionTitle>
                    <Badge type="missing">{errorData.missingKeys.length}</Badge>
                  </SectionHeader>
                  <ItemList>
                    {errorData.missingKeys.map((item, index) => (
                      <Item key={index} type="missing">
                        <ItemIcon type="missing" />
                        <ItemText type="missing">{item}</ItemText>
                      </Item>
                    ))}
                  </ItemList>
                </Section>
              )}

              {totalErrors === 0 && (
                <EmptyState>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ margin: "0 auto 12px" }}
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <div>No validation errors found</div>
                </EmptyState>
              )}
            </ContentWrapper>

            <Footer>
              <FooterText>
                Total Issues: <strong>{totalErrors}</strong>
              </FooterText>
              <FooterText>
                {errorData.duplicates.length} Duplicates •{" "}
                {errorData.missingKeys.length} Missing
              </FooterText>
              <div style={{ marginLeft: 12 }}>
                <PrimaryCTAButton
                  type="button"
                  onClick={() => {
                    const headers = [
                      ...(errorData.missingKeys || []),
                      ...(errorData.duplicates || []),
                    ];
                    if (!headers || headers.length === 0) return;
                    try {
                      const formattedHeaders = headers.map((header) => {
                        if (typeof header !== "string") return header;
                        return header
                          .replace(/\bmr\/mrs\b/gi, "Mr/Ms")
                          .replace(/\bmr\b/gi, "Mr")
                          .replace(/\bmrs\b/gi, "Ms")
                          .replace(/\b([a-z])/g, (char) => char.toUpperCase())
                          .replace(/\bAnd\b/g, "and")
                          .replace(/\bOf\b/g, "of")
                          .replace(/\bThe\b/g, "the");
                      });
                      const ws = XLSX.utils.aoa_to_sheet([formattedHeaders]);
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, "Template");
                      XLSX.writeFile(wb, "validation_template.xlsx");
                    } catch (err) {
                      console.error("Failed to generate XLSX:", err);
                    }
                  }}
                >
                  Download Template
                </PrimaryCTAButton>
              </div>
            </Footer>
          </Card>
        </Container>
      </DrawerContainer>
    </Modal>
  );
};

export default ValidationErrors;

const DrawerContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 656px;
  }
`;

const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(to right, #fee2e2, #fef2f2);
  padding: 20px 24px;
  border-bottom: 1px solid #fecaca;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(to bottom right, #ef4444, #dc2626);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(239, 68, 68, 0.3);
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const HeaderTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #991b1b;
  margin: 0 0 4px 0;
`;

const HeaderSubtitle = styled.p`
  font-size: 14px;
  color: #dc2626;
  margin: 0;
`;

const ContentWrapper = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 24px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.type === "duplicate" ? "#fef3c7" : "#dbeafe"};
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: ${(props) =>
    props.type === "duplicate" ? "#fbbf24" : "#3b82f6"};
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${(props) =>
    props.type === "duplicate" ? "#fef3c7" : "#dbeafe"};
  border: 1px solid
    ${(props) => (props.type === "duplicate" ? "#fde68a" : "#bfdbfe")};
  border-radius: 10px;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.type === "duplicate" ? "#fde68a" : "#bfdbfe"};
    transform: translateX(4px);
  }
`;

const ItemIcon = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) =>
    props.type === "duplicate" ? "#f59e0b" : "#2563eb"};
  flex-shrink: 0;
`;

const ItemText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.type === "duplicate" ? "#92400e" : "#1e40af")};
  font-family: "Monaco", "Courier New", monospace;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: #94a3b8;
  font-size: 14px;
`;

const Footer = styled.div`
  padding: 16px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FooterText = styled.span`
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
`;
